/**
 * 最小 .xlsx 写出器（零依赖，浏览器版）。
 *
 * 与 `backend/src/utils/xlsx.ts` 逐字同构，只差两处：TextEncoder 取浏览器全局、
 * 产物直接交给 Blob 存盘。改一边必须改另一边——三处本地导出（客户、核算月报、收支明细）
 * 在这里产表，而订单台账是服务端产的，两边打开必须是同一种样子。
 *
 * 为什么不用库：npm 上的 `xlsx`（SheetJS CE 0.18.5）带着两个已知漏洞（原型污染
 * CVE-2023-30533、ReDoS CVE-2024-22363，修复只进他们的商业 CDN 版本），`exceljs`
 * 则拖进十几层传递依赖，而这些都会原样进 vite bundle。我们要的只是「一张表、单元格分文本和数字」。
 *
 * 为什么值得换掉 CSV：CSV 没有类型，Excel 双击打开时逐格猜类型——数字按 double 只留
 * 15 位有效数字（16 位以上会被截成 0），前导 0 会被吃掉（0 开头的座机号改不回来）。
 * 这里把联系方式、订单号、日期一律写成**文本格**，金额写成**数字格**（可求和、带千分位），
 * 日期作为文本格也就再不会被区域设置重排成 2026/10/9。
 */

/** 单元格值：null / undefined / 空串都落成空格 */
export type XlsxCell = string | number | null | undefined;

export type XlsxCellKind = 'text' | 'number' | 'money';

export interface XlsxColumn {
  header: string;
  /** 列宽（Excel 的字符单位）。缺省按表头长度推算，长文本列由调用方给值 */
  width?: number;
  /**
   * text：文本格，Excel 不再猜类型（默认）。
   * number / money：数字格，money 套 `#,##0.00`；拿到非数字值时退回文本格，不静默丢数。
   */
  kind?: XlsxCellKind;
}

export interface XlsxTable {
  /** 工作表名，超 31 字符或含 []:*?/\ 时会被清洗 */
  sheetName?: string;
  /**
   * 表格上方的标题行。按客户导出的台账用它写「这份账是谁的」，
   * 于是客户姓名不必在每一行重复一遍（重复的列既占宽，又让人怀疑是不是有不同值）。
   */
  title?: string;
  /** 标题下方的说明行，逐行各占一行，灰字。 */
  metaLines?: readonly string[];
  columns: readonly XlsxColumn[];
  /** 每行按 columns 顺序给值 */
  rows: readonly (readonly XlsxCell[])[];
}

const DEFAULT_SHEET_NAME = 'Sheet1';
/** 下载端点共用的 Content-Type；四端要认这一个，别各自再抄一遍 */
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** 样式索引：0 常规、1 表头加粗、2 金额千分位、3 标题、4 灰字说明 */
const STYLE_HEADER = 1;
const STYLE_MONEY = 2;
const STYLE_TITLE = 3;
const STYLE_META = 4;
const MONEY_FORMAT_ID = 164;

/** XML 1.0 不允许的控制字符：真出现在用户数据里会让 Excel 判定文件损坏，直接剔除 */
// 这里要匹配的就是控制字符本身，规则与本意冲突，按行豁免；后端的镜像文件不带这行，因为那边没启用该规则
// eslint-disable-next-line no-control-regex
const ILLEGAL_XML_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

function escapeXml(value: string): string {
  return value
    .replace(ILLEGAL_XML_CHARS, '')
    .replace(/[&<>"]/g, (ch) => (ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;'));
}

function cleanSheetName(name: string | undefined): string {
  // Excel 不允许工作表名里有 [ ] : * ? / \
  const base = (name ?? '').replace(/[[\]:*?/\\]/g, '').trim();
  return (base || DEFAULT_SHEET_NAME).slice(0, 31);
}

/** 列序号 → A1 引用的列名（0→A … 25→Z、26→AA） */
function columnName(index: number): string {
  let n = index + 1;
  let name = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function columnWidth(header: string, explicit: number | undefined): number {
  if (explicit !== undefined) return Math.max(1, Math.min(255, explicit));
  // 表头多是两到五个汉字，按双宽估个下限再留余量
  return Math.max(11, Math.min(24, header.length * 2 + 5));
}

/** 数字格只接受有限数；拿到的不是数（脏值、空串）时返回 null 让调用方落回文本格 */
function numericValue(value: XlsxCell): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function cellXml(ref: string, kind: XlsxCellKind, value: XlsxCell, style = 0): string {
  // 金额列没显式给样式也要套千分位；表头那种显式样式优先
  const useStyle = style > 0 ? style : kind === 'money' ? STYLE_MONEY : 0;
  const styleAttr = useStyle > 0 ? ` s="${useStyle}"` : '';
  if (kind === 'number' || kind === 'money') {
    const n = numericValue(value);
    if (n !== null) return `<c r="${ref}"${styleAttr}><v>${n}</v></c>`;
    if (value === null || value === undefined || value === '') return '';
  }
  const text = String(value ?? '');
  if (text === '') return '';
  return `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
}

function buildWorksheet(table: XlsxTable): string {
  const { columns, rows } = table;
  const lastColumn = columnName(Math.max(0, columns.length - 1));

  // 标题与客户信息块各占一行，末尾再空一行把块和表头隔开。表头因此不再固定在第 1 行，
  // 于是冻结窗格、dimension、每一行的行号全部由 headerRow 推出来——写死 1 的话
  // 带块的台账会整列错位，Excel 打开只是「看起来少一行」，没人能一眼看出来。
  const preamble: { text: string; style: number }[] = [];
  if (table.title) preamble.push({ text: table.title, style: STYLE_TITLE });
  for (const meta of table.metaLines ?? []) preamble.push({ text: meta, style: STYLE_META });
  if (preamble.length > 0) preamble.push({ text: '', style: 0 });

  const headerRow = preamble.length + 1;
  const dimension = `A1:${lastColumn}${headerRow + rows.length}`;

  const cols = columns
    .map(
      (column, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${columnWidth(column.header, column.width)}" customWidth="1"/>`,
    )
    .join('');

  const preambleRows = preamble.map((item, i) => {
    const r = i + 1;
    return `<row r="${r}">${item.text ? cellXml(`A${r}`, 'text', item.text, item.style) : ''}</row>`;
  });

  const headerRowXml = `<row r="${headerRow}">${columns
    .map((column, i) => cellXml(`${columnName(i)}${headerRow}`, 'text', column.header, STYLE_HEADER))
    .join('')}</row>`;

  const bodyRows = rows.map((row, r) => {
    const index = headerRow + r + 1;
    return `<row r="${index}">${columns
      .map((column, i) => cellXml(`${columnName(i)}${index}`, column.kind ?? 'text', row[i]))
      .join('')}</row>`;
  });

  // 部件顺序由 CT_Worksheet 的 schema 序列规定；ignoredErrors 必须排在 sheetData 之后
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><dimension ref="${dimension}"/><sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="15"/><cols>${cols}</cols><sheetData>${preambleRows.join('')}${headerRowXml}${bodyRows.join('')}</sheetData><ignoredErrors><ignoredError sqref="${dimension}" numberStoredAsText="1"/></ignoredErrors></worksheet>`;
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`;

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;

/** 部件顺序是 schema 规定的：numFmts → fonts → fills → borders → cellStyleXfs → cellXfs → cellStyles */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="1"><numFmt numFmtId="${MONEY_FORMAT_ID}" formatCode="#,##0.00"/></numFmts><fonts count="4"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="14"/><name val="Calibri"/></font><font><sz val="10"/><color rgb="FF6B7280"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="5"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="${MONEY_FORMAT_ID}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;

function workbookXml(sheetName: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let bit = 0; bit < 8; bit++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** ZIP 时间戳是 DOS 打包格式、年份从 1980 起算，早于 1980 会写成非法值 */
function dosStamp(d: Date): { time: number; date: number } {
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

const LOCAL_HEADER_SIZE = 30;
const CENTRAL_HEADER_SIZE = 46;
const EOCD_SIZE = 22;

/**
 * 打成 ZIP。方法是 STORE（不压缩）：不压缩就不需要 deflate 实现，四端各自写一遍也只有这点量，
 * 代价是文件比压缩包大一些——台账是这个量级，换掉「装一个压缩库」不亏。
 */
function zipStore(entries: readonly ZipEntry[], now: Date): Uint8Array {
  const encoder = new TextEncoder();
  const { time, date } = dosStamp(now);
  const names = entries.map((entry) => encoder.encode(entry.name));
  const crcs = entries.map((entry) => crc32(entry.data));

  const localOffsets: number[] = [];
  let cursor = 0;
  entries.forEach((entry, i) => {
    localOffsets.push(cursor);
    cursor += LOCAL_HEADER_SIZE + names[i].length + entry.data.length;
  });
  const centralStart = cursor;
  names.forEach((name) => {
    cursor += CENTRAL_HEADER_SIZE + name.length;
  });
  const centralSize = cursor - centralStart;

  const out = new Uint8Array(cursor + EOCD_SIZE);
  const view = new DataView(out.buffer);
  let offset = 0;
  const u16 = (v: number) => {
    view.setUint16(offset, v & 0xffff, true);
    offset += 2;
  };
  const u32 = (v: number) => {
    view.setUint32(offset, v >>> 0, true);
    offset += 4;
  };
  const put = (bytes: Uint8Array) => {
    out.set(bytes, offset);
    offset += bytes.length;
  };

  entries.forEach((entry, i) => {
    u32(0x04034b50);
    u16(20); // version needed to extract
    u16(0x0800); // 文件名按 UTF-8 存
    u16(0); // method = STORE
    u16(time);
    u16(date);
    u32(crcs[i]);
    u32(entry.data.length);
    u32(entry.data.length);
    u16(names[i].length);
    u16(0); // extra field
    put(names[i]);
    put(entry.data);
  });

  entries.forEach((entry, i) => {
    u32(0x02014b50);
    u16(20); // version made by
    u16(20); // version needed
    u16(0x0800);
    u16(0); // method = STORE
    u16(time);
    u16(date);
    u32(crcs[i]);
    u32(entry.data.length);
    u32(entry.data.length);
    u16(names[i].length);
    u16(0); // extra
    u16(0); // file comment
    u16(0); // disk number start
    u16(0); // internal attributes
    u32(0); // external attributes
    u32(localOffsets[i]);
    put(names[i]);
  });

  u32(0x06054b50);
  u16(0); // this disk
  u16(0); // disk with central directory
  u16(entries.length);
  u16(entries.length);
  u32(centralSize);
  u32(centralStart);
  u16(0); // comment length

  return out;
}

/** 表 → .xlsx 字节。存盘由调用方交给 downloadBytes。 */
export function buildXlsx(table: XlsxTable, now: Date = new Date()): Uint8Array {
  if (table.columns.length === 0) throw new Error('导出表至少要有一列');
  const encoder = new TextEncoder();
  return zipStore(
    [
      { name: '[Content_Types].xml', data: encoder.encode(CONTENT_TYPES) },
      { name: '_rels/.rels', data: encoder.encode(ROOT_RELS) },
      { name: 'xl/workbook.xml', data: encoder.encode(workbookXml(cleanSheetName(table.sheetName))) },
      { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(WORKBOOK_RELS) },
      { name: 'xl/styles.xml', data: encoder.encode(STYLES) },
      { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(buildWorksheet(table)) },
    ],
    now,
  );
}
